import { useState, type KeyboardEvent } from "react";
import { useFormikContext } from "formik";
import moment from "moment/moment";
import { IconSearch } from "@tabler/icons-react";
import Button from "../../UI/Button";
import Callout from "../../UI/Callout";
import Card from "../../UI/Card";
import Status from "../../UI/Status";
import Text, { type Color } from "../../UI/Text";
// @ts-expect-error pure js
import TextInputStandalone from "../../UI/TextInputStandalone";
// @ts-expect-error pure js
import useDebouncedValue from "../../../hooks/use-debounced-value";
import useDpeLookup, {
  fetchDpeByNumber,
  type AdemeDpe,
} from "../../../hooks/use-dpe-lookup";
import {
  describePrefill,
  dpePrefillValues,
  gasPresence,
  type DpePrefillValues,
} from "../../../utils/ademeDpe";
import styles from "./DpePrefill.module.css";

interface DpePrefillFormValues extends DpePrefillValues {
  rue?: string;
  cp?: string;
  ville?: string;
}

const GRADE_COLORS: Record<string, Color> = {
  A: "success",
  B: "success",
  C: "success",
  D: "warning",
  E: "warning",
  F: "danger",
  G: "danger",
};

const formatDate = (date?: string) =>
  date ? moment(date).format("DD/MM/YYYY") : "";

function describe(dpe: AdemeDpe): string {
  return [
    dpe.type_batiment,
    dpe.surface_habitable_logement && `${dpe.surface_habitable_logement} m²`,
    dpe.complement_adresse_batiment,
    dpe.complement_adresse_logement,
  ]
    .filter(Boolean)
    .join(" · ");
}

function DpeCard({
  dpe,
  onSelect,
}: {
  dpe: AdemeDpe;
  onSelect: (dpe: AdemeDpe) => void;
}) {
  return (
    <Card padding="sm" className={styles.dpe}>
      <div className={styles.grades}>
        <Status
          color={GRADE_COLORS[dpe.etiquette_dpe] ?? "neutral"}
          text={`DPE ${dpe.etiquette_dpe}`}
          weight="bold"
        />
        {dpe.etiquette_ges && (
          <Status color="neutral" text={`GES ${dpe.etiquette_ges}`} />
        )}
      </div>
      <div className={styles.details}>
        <Text weight="medium">{describe(dpe)}</Text>
        <Text variant="deci">
          Établi le {formatDate(dpe.date_etablissement_dpe)}
          {dpe.date_fin_validite_dpe &&
            `, valable jusqu'au ${formatDate(dpe.date_fin_validite_dpe)}`}{" "}
          · n° {dpe.numero_dpe}
          {(dpe.annee_construction || dpe.periode_construction) &&
            ` · construction ${
              dpe.annee_construction ?? dpe.periode_construction
            }`}
        </Text>
      </div>
      <Button variant="secondary" size="sm" onClick={() => onSelect(dpe)}>
        Utiliser
      </Button>
    </Card>
  );
}

/**
 * En tête du formulaire d'un logement : propose les DPE enregistrés à l'ADEME
 * pour l'adresse tant qu'aucun indice n'est renseigné, avec une recherche par
 * numéro si le bon n'y est pas. Rien n'est affiché sans DPE à l'adresse. Le
 * DPE choisi préremplit la classe, la date du DPE, la surface, l'année de
 * construction, le chauffage, l'eau chaude, le type d'habitat, et dispense du
 * diagnostic gaz un logement sans gaz.
 */
export default function DpePrefill() {
  const { values, setFieldValue } = useFormikContext<DpePrefillFormValues>();
  const [applied, setApplied] = useState<AdemeDpe | null>(null);
  const [numero, setNumero] = useState("");
  const [byNumber, setByNumber] = useState<{
    loading: boolean;
    dpe?: AdemeDpe | null;
    error?: boolean;
  }>({ loading: false });
  const rue: string = useDebouncedValue(values.rue ?? "", 400);
  const cp: string = useDebouncedValue(values.cp ?? "", 400);
  const ville: string = useDebouncedValue(values.ville ?? "", 400);
  const { result } = useDpeLookup(
    { rue, cp, ville },
    !values.indice_dpe && !applied,
  );

  function select(dpe: AdemeDpe) {
    const next = dpePrefillValues(dpe, values);
    Object.entries(next).forEach(([field, value]) =>
      setFieldValue(field, value),
    );
    setApplied(dpe);
  }

  async function searchByNumber() {
    if (!numero.trim()) return;
    setByNumber({ loading: true });
    try {
      setByNumber({ loading: false, dpe: await fetchDpeByNumber(numero) });
    } catch {
      setByNumber({ loading: false, error: true });
    }
  }

  function onNumeroKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      searchByNumber();
    }
  }

  if (applied) {
    const summary = describePrefill(
      dpePrefillValues(applied, {}),
      gasPresence(applied),
    );
    return (
      <Callout
        variant="success"
        title={`Prérempli depuis le DPE n° ${applied.numero_dpe}`}
        action={
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => {
              setApplied(null);
              setFieldValue("indice_dpe", "");
            }}
          >
            Choisir un autre DPE
          </Button>
        }
      >
        Établi le {formatDate(applied.date_etablissement_dpe)} :{" "}
        {summary.join(", ")}. Vérifiez les champs ci-dessous.
      </Callout>
    );
  }

  if (
    values.indice_dpe ||
    result?.kind !== "found" ||
    result.dpes.length === 0
  ) {
    return null;
  }

  return (
    <Callout
      variant="info"
      title="Préremplir depuis le DPE (ADEME)"
      action={
        <div className={styles.body}>
          <div className={styles.list}>
            {result.dpes.map((dpe) => (
              <DpeCard key={dpe.numero_dpe} dpe={dpe} onSelect={select} />
            ))}
          </div>
          <div className={styles.search}>
            <TextInputStandalone
              type="text"
              size="sm"
              placeholder="N° de DPE, ex. 2357E1473341G"
              aria-label="Numéro de DPE"
              value={numero}
              onChange={(event: { target: { value: string } }) =>
                setNumero(event.target.value)
              }
              onKeyDown={onNumeroKeyDown}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={IconSearch}
              disabled={!numero.trim() || byNumber.loading}
              onClick={searchByNumber}
            >
              Rechercher
            </Button>
          </div>
          {byNumber.dpe && <DpeCard dpe={byNumber.dpe} onSelect={select} />}
          {byNumber.dpe === null && (
            <Callout variant="neutral">
              Aucun DPE enregistré à l'ADEME sous ce numéro depuis juillet 2021.
            </Callout>
          )}
          <Callout variant="danger">
            {byNumber.error &&
              "Impossible d'interroger l'observatoire des DPE pour le moment."}
          </Callout>
        </div>
      }
    >
      DPE enregistrés à l'adresse {result.address} : choisissez celui du
      logement, ou saisissez le numéro figurant sur votre DPE s'il n'y est pas.
    </Callout>
  );
}
